import React, { useState } from 'react';
import { 
  Box, 
  Tooltip, 
  CircularProgress, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Tabs, 
  Tab, 
  Typography, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow, 
  Paper, 
  Alert,
  Chip
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import JsonViewer from './JsonViewer';
import { runPipeHookTest } from '../api/pipeHookApi';
import { useAuth } from '../auth/AuthContext';

/**
 * PipeHookTestButton component provides a button to trigger the pipe_hook test
 * and displays results in a detailed dialog
 * 
 * @returns {JSX.Element} - PipeHookTestButton component
 */
const PipeHookTestButton = () => {
  const { idToken } = useAuth();
  const [testStatus, setTestStatus] = useState('idle'); // idle, running, completed, error
  const [testResults, setTestResults] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogTab, setDialogTab] = useState(0);
  const [lastRunTime, setLastRunTime] = useState(null);

  // Get color for test status
  const getStatusColor = (status) => {
    switch(status) {
      case 'completed':
        return testResults?.['test_passed'] ? '#4caf50' : '#f44336'; // Green if passed, red if failed
      case 'running':
        return '#ff9800'; // Orange
      case 'error':
        return '#f44336'; // Red
      default:
        return '#9e9e9e'; // Grey for idle
    }
  };

  // Get icon for test status
  const getStatusIcon = (status) => {
    switch(status) {
      case 'completed':
        return testResults?.['test_passed'] ? <CheckCircleIcon /> : <ErrorIcon />;
      case 'running':
        return <CircularProgress size={16} />;
      case 'error':
        return <ErrorIcon />;
      default:
        return <PlayArrowIcon />;
    }
  };

  // Get tooltip text
  const getTooltipText = () => {
    switch(testStatus) {
      case 'idle':
        return 'Click to run pipe_hook test (validates entire data pipeline)';
      case 'running':
        return 'Pipe hook test is running...';
      case 'completed':
        return testResults?.['test_passed'] ? 
          'Pipe hook test PASSED - pipeline is working correctly' : 
          'Pipe hook test FAILED - check details for issues';
      case 'error':
        return 'Error running pipe hook test';
      default:
        return 'Pipe hook test';
    }
  };

  // Handle running the test
  const handleRunTest = async () => {
    try {
      setTestStatus('running');
      setOpenDialog(true);
      setDialogTab(0);
      
      console.log('🚀 Starting pipe hook test...');
      const results = await runPipeHookTest(idToken);
      
      setTestResults(results);
      setTestStatus('completed');
      setLastRunTime(new Date().toISOString());
      
      console.log('✅ Pipe hook test completed:', results);
      
    } catch (error) {
      console.error('❌ Pipe hook test failed:', error);
      setTestResults({
        error: error.message,
        timestamp: new Date().toISOString(),
        'test_passed': false,
        'test_failed': true
      });
      setTestStatus('error');
      setLastRunTime(new Date().toISOString());
    }
  };

  // Handle dialog tab change
  const handleTabChange = (event, newValue) => {
    setDialogTab(newValue);
  };

  // Render test results dialog
  const renderTestResultsDialog = () => {
    if (!openDialog) return null;

    return (
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Pipe Hook Test Results
            <FiberManualRecordIcon 
              sx={{ 
                color: getStatusColor(testStatus),
                fontSize: 16,
                ml: 1,
                verticalAlign: 'middle'
              }} 
            />
          </Typography>
          <Button 
            startIcon={<RefreshIcon />} 
            onClick={handleRunTest}
            disabled={testStatus === 'running'}
            size="small"
          >
            Run Test
          </Button>
        </DialogTitle>
        
        <Tabs value={dialogTab} onChange={handleTabChange} sx={{ px: 2 }}>
          <Tab label="Summary" />
          <Tab label="Metrics" />
          <Tab label="Output" />
          <Tab label="Raw Data" />
        </Tabs>
        
        <DialogContent>
          {/* Summary Tab */}
          {dialogTab === 0 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>Test Summary</Typography>
              
              {/* Test Status */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Test Status</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip 
                    label={testResults?.['test_passed'] ? 'PASSED' : 'FAILED'}
                    color={testResults?.['test_passed'] ? 'success' : 'error'}
                    icon={getStatusIcon(testStatus)}
                  />
                  {testResults?.summary?.message && (
                    <Typography variant="body2">
                      {testResults.summary.message}
                    </Typography>
                  )}
                </Box>
              </Paper>

              {/* Test Information */}
              {testResults && (
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Test Information</Typography>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>Last Run</TableCell>
                        <TableCell>
                          {lastRunTime ? new Date(lastRunTime).toLocaleString() : 'Never'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Duration</TableCell>
                        <TableCell>
                          {testResults.duration_ms ? `${Math.round(testResults.duration_ms / 1000)}s` : 'N/A'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Test ID</TableCell>
                        <TableCell>
                          {testResults.details?.report?.test_id || 'N/A'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Device ID</TableCell>
                        <TableCell>
                          {testResults.details?.report?.device_id || 'N/A'}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>
              )}

              {/* Error Details */}
              {testResults?.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Error:</strong> {testResults.error}
                  </Typography>
                </Alert>
              )}

              {/* Test Description */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>What This Test Does</Typography>
                <Typography variant="body2" paragraph>
                  The pipe_hook test validates the complete real-time telemetry pipeline:
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  <li>MQTT telemetry ingestion</li>
                  <li>Database storage via sabal-cloud</li>
                  <li>Debezium CDC detection</li>
                  <li>Webhook event processing</li>
                  <li>Real-time service delivery via WebSocket</li>
                </Box>
                <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                  This test is completely self-contained and leaves no residue in the database.
                </Typography>
              </Paper>
            </Box>
          )}

          {/* Metrics Tab */}
          {dialogTab === 1 && testResults?.metrics && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>Test Metrics</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Metric</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Duration</TableCell>
                    <TableCell>{testResults.metrics.duration_seconds}s</TableCell>
                    <TableCell>
                      <FiberManualRecordIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Stages Completed</TableCell>
                    <TableCell>{testResults.metrics.stages_completed}/6</TableCell>
                    <TableCell>
                      <FiberManualRecordIcon 
                        sx={{ 
                          color: testResults.metrics.stages_completed === 6 ? '#4caf50' : '#ff9800',
                          fontSize: 16 
                        }} 
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Errors Count</TableCell>
                    <TableCell>{testResults.metrics.errors_count}</TableCell>
                    <TableCell>
                      <FiberManualRecordIcon 
                        sx={{ 
                          color: testResults.metrics.errors_count === 0 ? '#4caf50' : '#f44336',
                          fontSize: 16 
                        }} 
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>MQTT Connected</TableCell>
                    <TableCell>{testResults.metrics.mqtt_connected ? 'Yes' : 'No'}</TableCell>
                    <TableCell>
                      <FiberManualRecordIcon 
                        sx={{ 
                          color: testResults.metrics.mqtt_connected ? '#4caf50' : '#f44336',
                          fontSize: 16 
                        }} 
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>WebSocket Connected</TableCell>
                    <TableCell>{testResults.metrics.websocket_connected ? 'Yes' : 'No'}</TableCell>
                    <TableCell>
                      <FiberManualRecordIcon 
                        sx={{ 
                          color: testResults.metrics.websocket_connected ? '#4caf50' : '#f44336',
                          fontSize: 16 
                        }} 
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Database Verified</TableCell>
                    <TableCell>{testResults.metrics.database_verified ? 'Yes' : 'No'}</TableCell>
                    <TableCell>
                      <FiberManualRecordIcon 
                        sx={{ 
                          color: testResults.metrics.database_verified ? '#4caf50' : '#f44336',
                          fontSize: 16 
                        }} 
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Real-time Event Received</TableCell>
                    <TableCell>{testResults.metrics.realtime_event_received ? 'Yes' : 'No'}</TableCell>
                    <TableCell>
                      <FiberManualRecordIcon 
                        sx={{ 
                          color: testResults.metrics.realtime_event_received ? '#4caf50' : '#f44336',
                          fontSize: 16 
                        }} 
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}

          {/* Output Tab */}
          {dialogTab === 2 && testResults && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>Test Output</Typography>
              
              {testResults.details?.output && (
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Test Output (Last 20 lines)</Typography>
                  <Box 
                    sx={{ 
                      fontFamily: 'monospace', 
                      fontSize: '0.875rem',
                      backgroundColor: 'rgba(0, 0, 0, 0.03)',
                      p: 1,
                      borderRadius: 1,
                      maxHeight: '300px',
                      overflowY: 'auto',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {testResults.details.output}
                  </Box>
                </Paper>
              )}

              {testResults.details?.error && (
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Error Output (Last 10 lines)</Typography>
                  <Box 
                    sx={{ 
                      fontFamily: 'monospace', 
                      fontSize: '0.875rem',
                      backgroundColor: 'rgba(244, 67, 54, 0.1)',
                      p: 1,
                      borderRadius: 1,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      whiteSpace: 'pre-wrap',
                      color: 'error.main'
                    }}
                  >
                    {testResults.details.error}
                  </Box>
                </Paper>
              )}
            </Box>
          )}

          {/* Raw Data Tab */}
          {dialogTab === 3 && testResults && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>Raw Test Data</Typography>
              <JsonViewer json={testResults} />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <>
      <Tooltip title={getTooltipText()}>
        <Box
          onClick={handleRunTest}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            cursor: 'pointer',
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
              transform: 'scale(1.1)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          {getStatusIcon(testStatus)}
        </Box>
      </Tooltip>
      
      {/* Test Results Dialog */}
      {renderTestResultsDialog()}
    </>
  );
};

export default PipeHookTestButton;
